import React from "react";
import { useNavigate } from "react-router-dom";
import { useDuelsList } from "../../services/moodle/duel.queries";
import { isDuelsEnabled } from "../../utils/featureFlags";

/**
 * DuelBadge Component - Navbar lighthouse battle badge.
 *
 * Slice 3 (spec `duel-spa-navigation` — "DuelBadge Navigates To /duels",
 * spec `duel-data-layer` — "Shared Pending-Duels Query Key",
 * "Interval-Based Polling"): the badge no longer opens `BattleModal`
 * directly nor polls via a hand-rolled `setInterval`. It consumes the same
 * `useDuelsList` hook (and therefore the same react-query cache key) as
 * `/duels`'s `DuelsPage`, so both stay in sync, and clicking it navigates to
 * `/duels` instead of invoking a callback.
 *
 * @param {Object} props
 * @param {string} props.token
 * @param {number|string} [props.currentUserId]
 * @param {number} [props.courseid=0]
 */
export const DuelBadge = ({ token, currentUserId, courseid = 0 }) => {
  const navigate = useNavigate();
  const duelsEnabled = isDuelsEnabled();
  const { data: pendingDuels } = useDuelsList({
    token,
    courseid,
    currentUserId,
    enabled: duelsEnabled && !!token,
  });

  const pendingCount = duelsEnabled ? (pendingDuels || []).length : 0;

  return (
    <div
      className="duel-badge-wrapper"
      style={{
        position: "relative",
        alignItems: "center",
      }}
    >
      <button
        type="button"
        className="btn-duel-badge"
        onClick={() => navigate("/duels")}
        aria-label={duelsEnabled ? "Desafíos de Batalla 1v1" : "Desafíos de Batalla 1v1 (Próximamente)"}
        title={duelsEnabled ? "Desafíos de Batalla 1v1" : "Próximamente disponible"}
        style={{
          background: "none",
          border: "none",
          cursor: "pointer",
          fontSize: "22px",
          display: "flex",
          flexDirection: "column",
          gap: "3px",
          alignItems: "center",
          justifyContent: "center",
          padding: "4px 6px",
          borderRadius: "8px",
          transition: "transform 0.2s ease",
          position: "relative",
        }}
      >
        <svg
          data-testid="duel-badge-icon"
          viewBox="0 0 32 32"
          width="32"
          height="32"
          fill="none"
          aria-hidden="true"
          focusable="false"
          style={{
            display: "block",
            color: duelsEnabled ? "#ecb236" : "#b8c3d5",
            opacity: 1,
          }}
        >
          <path d="M1 6.5 9 9v4l-8 2.5ZM31 6.5 23 9v4l8 2.5Z" fill="currentColor" />
          <path d="m16 2 6 5H10Z" fill="currentColor" />
          <rect x="12" y="9" width="8" height="5" rx="0.5" fill="currentColor" />
          <path d="M12 17h8l2 12H10Z" fill="#f7eedc" />
          <rect x="9" y="15" width="14" height="2" rx="0.5" fill="currentColor" />
          <rect x="8" y="28" width="16" height="2" rx="0.5" fill="#f7eedc" />
        </svg>
        {!duelsEnabled && (
          <span
            data-testid="duel-badge-soon"
            className="duel-badge-soon"
            style={{
              position: "static",
              flexShrink: 0,
              backgroundColor: "#0f172a",
              color: "#ecb236",
              fontSize: "8.5px",
              fontWeight: 800,
              letterSpacing: "0.5px",
              borderRadius: "4px",
              padding: "1px 4px",
              border: "1px solid #ecb236",
              lineHeight: 1.1,
              boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
              textTransform: "uppercase",
              pointerEvents: "none",
            }}
          >
            Próx
          </span>
        )}
        {pendingCount > 0 && (
          <span
            data-testid="duel-badge-count"
            className="duel-badge-counter"
            style={{
              position: "absolute",
              top: "-2px",
              right: "-2px",
              backgroundColor: "#ecb236",
              color: "#000000",
              fontSize: "12px",
              fontWeight: "bold",
              borderRadius: "10px",
              minWidth: "18px",
              height: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 4px",
              border: "2px solid #0f172a",
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            {pendingCount}
          </span>
        )}
      </button>
    </div>
  );
};

export default DuelBadge;
